import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Person as PersonIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  ArrowBack as ArrowBackIcon,
  ViewList as ViewListIcon,
  Summarize as SummarizeIcon,
  Payment as PaymentIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { retornoService, ViewRetorno } from '../../services/saida';

interface RetornoVisualizarProps {
  id: number;
}

type ViewMode = 'normal' | 'resumo';

const RetornoVisualizar: React.FC<RetornoVisualizarProps> = ({ id }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<ViewMode>('resumo');
  const [dialogExcluir, setDialogExcluir] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'error' | 'success' });

  const { data: retorno, isLoading, error } = useQuery<ViewRetorno>({
    queryKey: ['retorno', id],
    queryFn: () => retornoService.buscarDetalhesRetorno(id),
    enabled: !!id,
  });

  const excluirMutation = useMutation({
    mutationFn: () => retornoService.excluirNotaRetorno(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saidas'] });
      navigate('/saidas');
    },
    onError: (err: any) => {
      const message = err?.response?.data || 'Erro ao excluir o retorno.';
      setSnackbar({ open: true, message: typeof message === 'string' ? message : 'Erro ao excluir o retorno.', severity: 'error' });
    },
  });

  const formatCurrency = (value: string | number) => {
    const numericValue = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : value;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(isNaN(numericValue) ? 0 : numericValue);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (error || !retorno) {
    const errorMessage = (error as any)?.response?.data?.title
      || (error as any)?.response?.data?.message
      || (error as any)?.message
      || 'Erro ao carregar a nota de retorno.';
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, flexDirection: 'column', alignItems: 'center' }}>
        <Typography color="error" gutterBottom>Erro ao carregar a nota de retorno.</Typography>
        <Typography color="error" variant="body2">{errorMessage}</Typography>
      </Box>
    );
  }

  const pagamento = retorno.pagamento;
  const produtos = retorno.produtos || [];
  const produtosResumidos = retorno.produtosResumidos || [];
  const produtosOrdenados = [...produtos].sort((a, b) =>
    (a.referencia || '').localeCompare(b.referencia || '', 'pt-BR', { numeric: true, sensitivity: 'base' })
  );
  const produtosResumidosOrdenados = [...produtosResumidos].sort((a, b) =>
    (a.referencia || '').localeCompare(b.referencia || '', 'pt-BR', { numeric: true, sensitivity: 'base' })
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header */}
      <Paper sx={{ p: 3, backgroundColor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
              Retorno #{retorno.id} - Saída #{retorno.idSaida}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Cliente: {retorno.nomeVendedor || '-'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label="Visualização"
              color="secondary"
              variant="outlined"
              sx={{
                borderColor: 'white',
                color: 'white',
                '& .MuiChip-label': { color: 'white' },
              }}
            />
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, value) => value && setViewMode(value)}
              size="small"
              sx={{
                backgroundColor: 'white',
                '& .MuiToggleButton-root': {
                  color: 'primary.main',
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    color: 'white',
                  },
                },
              }}
            >
              <ToggleButton value="normal">
                <ViewListIcon sx={{ mr: 0.5, fontSize: 18 }} />
                Normal
              </ToggleButton>
              <ToggleButton value="resumo">
                <SummarizeIcon sx={{ mr: 0.5, fontSize: 18 }} />
                Resumo
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* Dados Principais */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Dados do Retorno
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Promotor de Vendas</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {retorno.nomePromotor || '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">Cliente</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {retorno.nomeVendedor || '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">CPF</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {retorno.cpf || '-'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Praça e Datas */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              <LocationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Praça e Datas
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Praça</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {retorno.praca || '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">Data de Saída</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {retorno.dataSaidaString || '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">Data de Retorno</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {retorno.dataRetornoString || '-'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Produtos */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Produtos do Retorno
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {viewMode === 'normal' ? (
                    <>
                      <TableCell sx={{ fontWeight: 'bold' }}>Código de Barras</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Referência</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Produto</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Preço de Venda</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell sx={{ fontWeight: 'bold' }}>Referência</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Produto</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Valor Vendido</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Qtd. Vendida</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Qtd. Devolvida</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {viewMode === 'normal' ? (
                  produtosOrdenados.length > 0 ? (
                    produtosOrdenados.map((produto, index) => (
                      <TableRow key={`${produto.idProduto}-${index}`}>
                        <TableCell>{produto.codigoBarras || '-'}</TableCell>
                        <TableCell>{produto.referencia}</TableCell>
                        <TableCell>{produto.nomeProduto}</TableCell>
                        <TableCell>{formatCurrency(produto.precoVenda)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography sx={{ py: 4, color: 'text.secondary' }}>
                          Nenhum produto encontrado neste retorno.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
                ) : produtosResumidosOrdenados.length > 0 ? (
                  produtosResumidosOrdenados.map((produto, index) => (
                    <TableRow key={`${produto.id}-${index}`}>
                      <TableCell>{produto.referencia}</TableCell>
                      <TableCell>{produto.nomeProduto}</TableCell>
                      <TableCell>{formatCurrency(produto.precoVenda)}</TableCell>
                      <TableCell>{produto.quantidadeVendida}</TableCell>
                      <TableCell>{produto.quantidadeRetorno}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography sx={{ py: 4, color: 'text.secondary' }}>
                        Nenhum produto encontrado neste retorno.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Pagamento */}
      {pagamento && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              <PaymentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Acerto e Pagamentos
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Total da Venda</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{formatCurrency(pagamento.totalDaVenda)}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">% Vendedora</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{pagamento.percentualVendedora}%</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Valor a Ser Pago</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{formatCurrency(pagamento.valorASerPago)}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Total Pago</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{formatCurrency(pagamento.totalPago)}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Saldo</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', color: pagamento.saldo > 0 ? 'error.main' : 'success.main' }}>
                  {formatCurrency(pagamento.saldo)}
                </Typography>
              </Box>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Valor Pago</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Forma de Pagamento</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Data do Pagamento</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Data de Inclusão</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagamento.pagamentosItens && pagamento.pagamentosItens.length > 0 ? (
                    pagamento.pagamentosItens.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{formatCurrency(item.valorPago)}</TableCell>
                        <TableCell>{item.formaPagamento}</TableCell>
                        <TableCell>{item.dataPagamentoString}</TableCell>
                        <TableCell>{item.dataInclusaoString}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography sx={{ py: 4, color: 'text.secondary' }}>
                          Nenhum pagamento registrado.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Botões */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/saidas')}
          sx={{ minWidth: 150 }}
        >
          Voltar para Lista
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setDialogExcluir(true)}
          sx={{ minWidth: 150 }}
        >
          Excluir Retorno
        </Button>
      </Box>

      {/* Dialog de confirmação de exclusão */}
      <Dialog open={dialogExcluir} onClose={() => setDialogExcluir(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Excluir Retorno</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deseja realmente realizar a exclusão do retorno?
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>
            Para visualizar itens excluídos selecione a opção visualizar nota excluída dentro do retorno.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogExcluir(false)} disabled={excluirMutation.isPending}>
            Cancelar
          </Button>
          <Button
            color="error"
            onClick={() => excluirMutation.mutate()}
            disabled={excluirMutation.isPending}
          >
            {excluirMutation.isPending ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RetornoVisualizar;
